import React, { useState } from 'react';
import { MDXProvider, MDXProviderComponentsProp } from '@mdx-js/react';
import { withPrefix } from 'gatsby';
// @ts-ignore
import { preToCodeBlock } from 'mdx-utils';
import { Code, CodeBlock } from './Code';
import { Heading, HeadingProps } from './Heading';
import { Link, LinkProps } from './Link';
import { Aside, AsideProps } from './Aside';
import { Pagination } from './Pagination';
import { Table, TableProps } from './Table';
import { ContentMenu } from './ContentMenu';
import { mergeSelectors } from './utils';

interface MDXTemplateProps {
  children: React.ReactNode;
  path: string;
}

/**
 * A helper function that converts Markdown elements into custom components
 */
const components = {
  // Custom code blocks with syntax highlighting
  pre: (preProps: React.HTMLAttributes<HTMLPreElement>) => {
    const props = preToCodeBlock(preProps);
    if (props) {
      return <Code {...props} />;
    } else {
      return <pre {...preProps} />;
    }
  },
  table: (props: TableProps) => <Table {...props} />,
  a: (props: LinkProps) => <Link {...props} />,
  img: ({
    src,
    alt,
    ...props
  }: React.DetailedHTMLProps<
    React.ImgHTMLAttributes<HTMLImageElement>,
    HTMLImageElement
  >) => <img {...props} src={withPrefix(src ?? '')} alt={alt} />,
  aside: (props: AsideProps) => <Aside {...props} />,
  CodeBlock,
  h2: (props: HeadingProps) => <Heading {...props} type="h2" />,
  h3: (props: HeadingProps) => <Heading {...props} type="h3" />,
  h4: (props: HeadingProps) => <Heading {...props} type="h4" />,
};

/**
 * A custom layout for MDX pages only, powered by the @mdx-js/react MDXProvider
 * and reusable components for markdown.
 */
export function MDXTemplate({ path, children }: MDXTemplateProps) {
  // Only render the ContentMenu if there are more than two <h2> or <h3> tags
  const [headingCount, setHeadingCount] = useState<null | number>(null);
  const [showMenu, setShowMenu] = useState(true);

  return (
    <MDXProvider
      components={components as unknown as MDXProviderComponentsProp}>
      <div
        id="docs-body"
        className={mergeSelectors([
          headingCount && headingCount > 2 ? 'grid' : undefined,
          !showMenu ? 'menu-hidden' : undefined,
        ])}
        style={headingCount === null ? { visibility: 'hidden' } : undefined}>
        <div className="markdown" style={{ overflow: 'hidden' }}>
          {children}
          {path ? <Pagination path={path} pageTitle /> : null}
        </div>
        <button
          className={mergeSelectors([
            'toc-button reveal',
            !showMenu ? 'active' : undefined,
          ])}
          onClick={() => setShowMenu(true)}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16">
            <path strokeWidth="2" fill="none" d="M14 6 L8 12 L2 6" />
          </svg>
        </button>
        <div className="page-contents">
          <ContentMenu
            hideMenu={() => setShowMenu(false)}
            pathname={path}
            headings={['h2', 'h3']}
            headingCount={setHeadingCount}
          />
        </div>
      </div>
    </MDXProvider>
  );
}
